import * as i0 from '@angular/core';
import { Component, Input, ViewChild, EventEmitter, Output, NgModule } from '@angular/core';
import { WysiwygComponent } from '@slidebean/ui/wysiwyg';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@slidebean/ui';
import { WysiwygModule } from '@slidebean/ui';
import isEqual from 'fast-deep-equal';
import JSpreadsheet from 'jspreadsheet-ce';

function clone(data) {
    return data && data.length && data[0].length
        ? data.slice().map((row) => row.slice())
        : [['']];
}

function isNil(data) {
    return data === null || typeof data === 'undefined';
}
function maxes(data) {
    let maxRow = 0;
    let maxCol = 0;
    const rowCount = data.length;
    const colCount = data[0]?.length ?? 0;
    for (let row = 0; row < rowCount; row++) {
        for (let col = 0; col < colCount; col++) {
            if (data[row][col]) {
                if (col > maxCol)
                    maxCol = col;
                if (row > maxRow)
                    maxRow = row;
            }
        }
    }
    return { maxRow, maxCol };
}
function shrink(data) {
    const shrunken = [];
    if (!data || data.length === 0)
        return shrunken;
    const { maxRow, maxCol } = maxes(data);
    for (let row = 0; row < maxRow + 1; row++) {
        const cells = [];
        for (let col = 0; col < maxCol + 1; col++) {
            cells.push(!isNil(data[row][col]) ? data[row][col] : '');
        }
        shrunken.push(cells);
    }
    return shrunken;
}

function transpose(data) {
    const transposed = [];
    for (let x = 0; x < data[0].length; x++) {
        transposed.push(Array(data.length));
    }
    for (let x = 0; x < data.length; x++) {
        for (let y = 0; y < data[0].length; y++) {
            transposed[y][x] = data[x][y];
        }
    }
    return transposed;
}

const INSERT_COLUMN_BEFORE = 'insert-column-before';
const INSERT_COLUMN_AFTER = 'insert-column-after';
const DELETE_COLUMN = 'delete-column';
const INSERT_ROW_BEFORE = 'insert-row-before';
const INSERT_ROW_AFTER = 'insert-row-after';
const DELETE_ROW = 'delete-row';
const SEPARATOR = 'separator';
const TRANSPOSE = 'transpose';
const areColumnItemsDisabled = (target, selection, dimensions) => isNaN(target.x) || selection.columns.length === dimensions.columns;
const areRowItemsDisabled = (target, selection, dimensions) => isNaN(target.y) || selection.rows.length === dimensions.rows;
const insertColumnBefore = ({ sheet, target, selection, dimensions, }) => ({
    title: 'Insert column left',
    disabled: areColumnItemsDisabled(target, selection, dimensions),
    onclick: () => {
        if (selection.columns.length > 1)
            sheet.insertColumn(1, selection.columns[0], true);
        else
            sheet.insertColumn(1, target.x, true);
    },
});
const insertColumnAfter = ({ sheet, target, selection, dimensions, }) => ({
    title: 'Insert column right',
    disabled: areColumnItemsDisabled(target, selection, dimensions),
    onclick: () => {
        if (selection.columns.length > 1)
            sheet.insertColumn(1, selection.columns[selection.columns.length - 1], false);
        else
            sheet.insertColumn(1, target.x, false);
    },
});
const deleteColumn = ({ sheet, target, selection, dimensions, }) => ({
    title: selection.columns.length > 1 ? 'Delete columns' : 'Delete column',
    disabled: areColumnItemsDisabled(target, selection, dimensions),
    onclick: () => {
        if (selection.columns.length > 0)
            sheet.deleteColumn();
        else
            sheet.deleteColumn(target.x, 1);
    },
});
const insertRowBefore = ({ sheet, target, selection, dimensions, }) => ({
    title: 'Insert row above',
    disabled: areRowItemsDisabled(target, selection, dimensions),
    onclick: () => {
        if (selection.rows.length > 1)
            sheet.insertRow(1, selection.rows[0], true);
        else
            sheet.insertRow(1, target.y, true);
    },
});
const insertRowAfter = ({ sheet, target, selection, dimensions, }) => ({
    title: 'Insert row below',
    disabled: areRowItemsDisabled(target, selection, dimensions),
    onclick: () => {
        if (selection.rows.length > 1)
            sheet.insertRow(1, selection.rows[selection.rows.length - 1], false);
        else
            sheet.insertRow(1, target.y, false);
    },
});
const deleteRow = ({ sheet, target, selection, dimensions, }) => ({
    title: selection.rows.length > 1 ? 'Delete rows' : 'Delete row',
    disabled: isNaN(target.y) || selection.rows.length === dimensions.rows,
    onclick: () => {
        if (selection.rows.length > 0)
            sheet.deleteRow();
        else
            sheet.deleteRow(target.y, 1);
    },
});
const separator = () => ({ type: 'line' });
const transposeData = ({ sheet }) => ({
    title: 'Transpose data',
    disabled: !sheet.transpose,
    onclick: () => {
        if (!sheet.transpose)
            return;
        sheet.transpose();
    },
});
const factories = {
    [INSERT_COLUMN_BEFORE]: insertColumnBefore,
    [INSERT_COLUMN_AFTER]: insertColumnAfter,
    [DELETE_COLUMN]: deleteColumn,
    [INSERT_ROW_BEFORE]: insertRowBefore,
    [INSERT_ROW_AFTER]: insertRowAfter,
    [DELETE_ROW]: deleteRow,
    [SEPARATOR]: separator,
    [TRANSPOSE]: transposeData,
};
const DEFAULT_MENU_ITEMS = [
    INSERT_ROW_BEFORE,
    INSERT_ROW_AFTER,
    SEPARATOR,
    INSERT_COLUMN_BEFORE,
    INSERT_COLUMN_AFTER,
    SEPARATOR,
    DELETE_ROW,
    DELETE_COLUMN,
    SEPARATOR,
    TRANSPOSE,
];
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const isToken = (token) => typeof token === 'string';
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const isFactory = (factory) => typeof factory === 'function';
const createMenuItem = (token, specs) => {
    const factory = factories[token];
    return factory ? factory(specs) : void 0;
};

const createContextMenu = (items) => (sheet, colIndex, rowIndex) => {
    const x = parseInt(colIndex ?? '', 10);
    const y = parseInt(rowIndex ?? '', 10);
    if (isNaN(x) || isNaN(y))
        return [];
    const target = { x, y };
    const dimensions = {
        rows: (sheet.options.data ?? []).length,
        columns: (sheet.options.columns ?? []).length,
    };
    const selection = {
        columns: sheet.getSelectedColumns() ?? [],
        rows: (sheet.getSelectedRows() ?? []).map(r => typeof r === 'number'
            ? r
            : parseInt(r.getAttribute('data-y') ?? '0', 10)),
    };
    return (items || [])
        .filter(item => item)
        .map(item => {
        const specs = { sheet, target, dimensions, selection };
        if (isToken(item))
            return createMenuItem(item, specs);
        if (isFactory(item))
            return item(specs);
        return item;
    })
        .filter(Boolean);
};

// taken from https://github.com/handsontable/handsontable/blob/master/lib/autoResize/autoResize.js
const body = document.body;
function observe(element, event, handler) {
    if (!element)
        return;
    element.addEventListener(event, handler, false);
}
function unobserve(element, event, handler) {
    if (!element)
        return;
    element.removeEventListener(event, handler, false);
}
function getComputedStyle(element) {
    return document?.defaultView?.getComputedStyle(element);
}
function px(value) {
    return `${value}px`;
}
function resolveDimension(value, inherited, defaultValue) {
    if (value === 'inherit')
        return inherited;
    const dimension = parseInt(value, 10);
    return !isNaN(dimension) ? dimension : defaultValue;
}
function getContent(element) {
    return element['value'] || element.innerText;
}
function createAutoResizer() {
    let el;
    const text = document.createTextNode('');
    const span = document.createElement('span');
    const dimensions = {
        minHeight: 200,
        maxHeight: 300,
        minWidth: 100,
        maxWidth: 300,
    };
    const resize = (char) => {
        if (!el)
            return;
        char = !char ? '' : !/^[a-zA-Z \.,\\\/\|0-9]$/.test(char) ? '.' : '';
        text.textContent = getContent(el) + char;
        const style = getComputedStyle(el);
        if (style) {
            span.style.fontSize = style.fontSize;
            span.style.fontFamily = style.fontFamily;
        }
        span.style.whiteSpace = 'pre';
        body.appendChild(span);
        const width = span.clientWidth + 2;
        body.removeChild(span);
        el.style.height = px(dimensions.minHeight);
        el.style.width = px(dimensions.minWidth > width
            ? dimensions.minWidth
            : width > dimensions.maxWidth
                ? dimensions.maxWidth
                : width);
        const scrollHeight = el.scrollHeight ? el.scrollHeight - 1 : 0;
        el.style.height = px(dimensions.minHeight > scrollHeight
            ? dimensions.minHeight
            : dimensions.maxHeight < scrollHeight
                ? dimensions.maxHeight
                : scrollHeight);
        if (dimensions.maxHeight < scrollHeight)
            el.style.overflowY = 'visible';
    };
    const delayedResize = () => requestAnimationFrame(resize);
    const extendDimensions = (config) => {
        if (config && config.minHeight)
            dimensions.minHeight = resolveDimension(config.minHeight, el.clientHeight, dimensions.minHeight);
        if (config && config.maxHeight)
            dimensions.maxHeight = resolveDimension(config.maxHeight, el.clientHeight, dimensions.maxHeight);
        if (config && config.minWidth)
            dimensions.minWidth = resolveDimension(config.minWidth, el.clientWidth, dimensions.minWidth);
        if (config && config.maxWidth)
            dimensions.maxWidth = resolveDimension(config.maxWidth, el.clientWidth, dimensions.maxWidth);
        if (span.firstChild)
            return;
        span.className = 'auto-resize';
        span.style.display = 'inline-block';
        span.appendChild(text);
    };
    const init = (target, config, doObserve) => {
        el = target;
        extendDimensions(config);
        if (el.nodeName === 'TEXTAREA') {
            el.style.resize = 'none';
            el.style.overflowY = '';
            el.style.height = px(dimensions.minHeight);
            el.style.minWidth = px(dimensions.minWidth);
            el.style.maxWidth = px(dimensions.maxWidth);
            el.style.overflowY = 'hidden';
        }
        else {
            el.style.minHeight = px(dimensions.minHeight);
            el.style.minWidth = px(dimensions.minWidth);
        }
        if (doObserve) {
            observe(el, 'change', resize);
            observe(el, 'cut', delayedResize);
            observe(el, 'paste', delayedResize);
            observe(el, 'drop', delayedResize);
            observe(el, 'keydown', delayedResize);
            observe(el, 'focus', resize);
            observe(el, 'compositionstart', delayedResize);
            observe(el, 'compositionupdate', delayedResize);
            observe(el, 'compositionend', delayedResize);
        }
        resize();
    };
    return {
        init,
        resize,
        unobserve: () => {
            unobserve(el, 'change', resize);
            unobserve(el, 'cut', delayedResize);
            unobserve(el, 'paste', delayedResize);
            unobserve(el, 'drop', delayedResize);
            unobserve(el, 'keydown', delayedResize);
            unobserve(el, 'focus', resize);
            unobserve(el, 'compositionstart', delayedResize);
            unobserve(el, 'compositionupdate', delayedResize);
            unobserve(el, 'compositionend', delayedResize);
        },
    };
}

class CellEditorComponent {
    constructor(el, zone) {
        this.el = el;
        this.zone = zone;
        this.useWysiwyg = true;
        this.resizer = createAutoResizer();
        this.customKeyBinding = {
            enter: {
                key: 'Enter',
                handler: () => {
                    return false; // block default quill new line on enter
                },
            },
        };
    }
    get element() {
        return this.el.nativeElement;
    }
    getValue() {
        if (this.wysiwyg)
            return this.wysiwyg.getValue();
        if (this.textarea)
            return this.textarea.nativeElement.value;
        return '';
    }
    setValue(value) {
        if (this.wysiwyg) {
            this.wysiwyg.setValue(value);
            return;
        }
        if (this.textarea) {
            this.textarea.nativeElement.value = value;
            return;
        }
    }
    show(cell, sheet) {
        if (!cell || !sheet)
            return;
        const top = cell.offsetTop - sheet.content.scrollTop;
        const left = cell.offsetLeft - sheet.content.scrollLeft;
        this.move(top, left);
        this.element.style.display = 'block';
        const input = this.getInputElement();
        if (!input)
            return;
        const clientWidth = sheet.table.clientWidth > sheet.content.clientWidth
            ? sheet.content.clientWidth
            : sheet.table.clientWidth;
        const minHeight = cell.offsetHeight;
        const minWidth = cell.offsetWidth;
        const maxHeight = (window.innerHeight || document.documentElement.clientHeight) - top;
        const maxWidth = clientWidth - left;
        this.zone.runOutsideAngular(() => this.resizer.init(input, { minHeight, maxHeight, minWidth, maxWidth }, !this.wysiwyg));
    }
    hide() {
        this.element.style.display = 'none';
        this.element.style.removeProperty('top');
        this.element.style.removeProperty('left');
        const input = this.getInputElement();
        if (!input)
            return;
        input.style.removeProperty('minHeight');
        input.style.removeProperty('minWidth');
        this.resizer.unobserve();
    }
    move(top, left) {
        this.element.style.top = `${top}px`;
        this.element.style.left = `${left}px`;
    }
    focus() {
        if (this.wysiwyg) {
            this.wysiwyg.focus();
            return;
        }
        if (this.textarea) {
            this.textarea.nativeElement.focus();
            return;
        }
    }
    blur() {
        if (this.wysiwyg) {
            this.wysiwyg.blur();
            return;
        }
        if (this.textarea) {
            this.textarea.nativeElement.blur();
            return;
        }
    }
    onWysiwygChange() {
        this.resizer.resize();
    }
    getInputElement() {
        if (this.wysiwyg)
            return this.wysiwyg.editor.element;
        if (this.textarea)
            return this.textarea.nativeElement;
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CellEditorComponent, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.3", type: CellEditorComponent, selector: "sb-cell-editor", inputs: { useWysiwyg: "useWysiwyg" }, viewQueries: [{ propertyName: "textarea", first: true, predicate: ["textarea"], descendants: true }, { propertyName: "wysiwyg", first: true, predicate: WysiwygComponent, descendants: true }], ngImport: i0, template: `
    <textarea
      #textarea
      *ngIf="!useWysiwyg"
      class="cell-editor__textarea rounded-0 raised raised--sm raised--active"
    ></textarea>
    <sb-wysiwyg
      legacyInputUI
      *ngIf="useWysiwyg"
      [toolbarButtonsWrap]="false"
      [keyBindings]="customKeyBinding"
      formControlClass="rounded-0 raised raised--sm raised--active"
      (onChange)="onWysiwygChange()"
    ></sb-wysiwyg>
  `, isInline: true, styles: [".cell-editor__textarea{border:none;outline-width:0;margin:0;padding:3px 5px;font-family:inherit;font-size:inherit;box-shadow:inset 0 0 0 1px #52bef2;display:block;color:#596377;background-color:#fff;box-sizing:content-box}:host::ng-deep sb-quill-input{box-sizing:content-box;height:auto;padding:3px 5px;border-radius:0}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.WysiwygComponent, selector: "sb-wysiwyg", inputs: ["placeholder", "minHeight", "textSizeBase", "toolbarButtonsWrap", "toolbarAlignment", "defaultAlignment", "textSizes", "colorSet", "alignments", "styles", "lists", "keyBindings", "history", "legacyInputUI", "isToolbarOpen", "value", "textSizeEnabled", "alignmentEnabled"], outputs: ["onChange", "onFocus", "onBlur"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CellEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-cell-editor', template: `
    <textarea
      #textarea
      *ngIf="!useWysiwyg"
      class="cell-editor__textarea rounded-0 raised raised--sm raised--active"
    ></textarea>
    <sb-wysiwyg
      legacyInputUI
      *ngIf="useWysiwyg"
      [toolbarButtonsWrap]="false"
      [keyBindings]="customKeyBinding"
      formControlClass="rounded-0 raised raised--sm raised--active"
      (onChange)="onWysiwygChange()"
    ></sb-wysiwyg>
  `, styles: [".cell-editor__textarea{border:none;outline-width:0;margin:0;padding:3px 5px;font-family:inherit;font-size:inherit;box-shadow:inset 0 0 0 1px #52bef2;display:block;color:#596377;background-color:#fff;box-sizing:content-box}:host::ng-deep sb-quill-input{box-sizing:content-box;height:auto;padding:3px 5px;border-radius:0}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }], propDecorators: { useWysiwyg: [{
                type: Input
            }], textarea: [{
                type: ViewChild,
                args: ['textarea']
            }], wysiwyg: [{
                type: ViewChild,
                args: [WysiwygComponent]
            }] } });

function hasCurrent(jexcel) {
    return !!(jexcel && jexcel.current);
}
function isEditing(jexcel) {
    return !!(hasCurrent(jexcel) && jexcel.current.edition);
}
function hasOpenCustomEditor(jexcel) {
    if (!isEditing(jexcel) ||
        !jexcel.current.options.columns ||
        !jexcel.current.edition) {
        return false;
    }
    const col = parseInt(jexcel.current.edition[2], 10);
    if (isNaN(col))
        return false;
    return !!jexcel.current.options.columns[col].editor;
}
function isEditable(jexcel) {
    return !!(hasCurrent(jexcel) && jexcel.current.options.editable === true);
}
function hasContextMenu(jexcel) {
    return !!(hasCurrent(jexcel) &&
        jexcel.current.contextMenu &&
        jexcel.current.contextMenu.contextmenu);
}
function hasHighlightedCells(jexcel) {
    return !!(hasCurrent(jexcel) &&
        jexcel.current.highlighted &&
        jexcel.current.highlighted.length);
}
function hasSelectedCell(jexcel) {
    return !!(hasCurrent(jexcel) && jexcel.current.selectedCell);
}

function isOutsideViewFrame(element) {
    if (!element)
        return [false, false, null];
    const position = element.getBoundingClientRect();
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;
    // Only check the right and bottom sides, the context menu won't be outside of left or top sides.
    return [position.right > width, position.bottom > height, position];
}
function positionIntoView(element) {
    const [x, y, position] = isOutsideViewFrame(element);
    if (x && position)
        element.style.left = `${position.left - position.width}px`;
    if (y && position)
        element.style.top = `${position.top - position.height}px`;
}
function hasCellDataAttributes(element) {
    return !!element.getAttribute('data-x') || !!element.getAttribute('data-y');
}
function findParentCell(element) {
    if (!element)
        return null;
    return !hasCellDataAttributes(element)
        ? findParentCell(element.parentElement)
        : element;
}
function findMenuElement(jexcel) {
    return hasContextMenu(jexcel)
        ? jexcel.current.contextMenu.contextmenu.menu
        : null;
}
function findParentWithTransform(element) {
    if (!element)
        return null;
    const style = window.getComputedStyle(element);
    return style.transform && style.transform !== 'none'
        ? element
        : findParentWithTransform(element.parentElement);
}
function hideContextMenuSection(jexcel) {
    if (!hasCurrent(jexcel))
        return;
    jexcel.current.contextMenu.style.display = 'none';
}
function showContextMenuSection(jexcel) {
    if (!hasCurrent(jexcel))
        return;
    jexcel.current.contextMenu.style.display = '';
}
function positionForParentWithTransform(parent, menu, e) {
    if (!parent || !menu || !e)
        return;
    const container = parent.getBoundingClientRect();
    const left = e.clientX - container.left;
    const top = e.clientY - container.top;
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    const [x, y, position] = isOutsideViewFrame(menu);
    if (y && position)
        menu.style.top = `${top - position.height}px`;
    if (x && position)
        menu.style.left = `${left - position.width}px`;
}
function patchContextMenuControls(jexcel) {
    const contextMenuControls = jexcel.contextMenuControls;
    jexcel.contextMenuControls = (e) => {
        // This piece of code makes the jexcel custom context menu appears when
        // the context menu is opened in a cell with html elements.
        const cell = findParentCell(e.target);
        if (cell && cell !== e.target) {
            e.preventDefault();
            cell.dispatchEvent(new MouseEvent(e.type, e));
            return;
        }
        // This piece of code makes the highlight to change with the context menu click
        // if the click was made in a non-highlighted cell or header.
        if (cell && !cell.classList.contains('highlight')) {
            cell.dispatchEvent(new MouseEvent('mousedown', { buttons: 1, button: 1, bubbles: true }));
        }
        // Hide the context menu section so it won't trigger the scroll
        // when is displayed in within an element with a css transformation
        // altering the fixed positioning.
        hideContextMenuSection(jexcel);
        contextMenuControls(e);
        showContextMenuSection(jexcel);
        const menu = findMenuElement(jexcel);
        if (!menu)
            return;
        const transformed = findParentWithTransform(menu);
        if (transformed)
            positionForParentWithTransform(transformed, menu, e);
        else
            positionIntoView(menu);
        menu.focus();
    };
    return jexcel;
}

function getCellIndexes(cell) {
    if (!cell)
        return void 0;
    const row = parseInt(`${cell[1] ?? ''}`, 10);
    const column = parseInt(`${cell[0] ?? ''}`, 10);
    return { row, column };
}
function withoutExtraMetaKeys(e) {
    return !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey;
}
function withControOrMetaKey(e) {
    return e.ctrlKey || e.metaKey;
}
function isEscapeKeyPressed(e) {
    return e.key === 'Escape' && withoutExtraMetaKeys(e);
}
function isEnterKeyPressed(e) {
    return e.key === 'Enter' && withoutExtraMetaKeys(e);
}
function isTabKeyPressed(e) {
    return e.key === 'Tab' && withoutExtraMetaKeys(e);
}
function isRedoHotkeyPressed(e) {
    return e.shiftKey && withControOrMetaKey(e) && e.key === 'z';
}
function isUndoHotkeyPressed(e) {
    return withControOrMetaKey(e) && e.key === 'z';
}
function isHistoryEnabled(jexcel) {
    return hasCurrent(jexcel) && !jexcel.current.ignoreHistory;
}
function isDeleteKeyPressed(e) {
    return ((e.key === 'Backspace' || e.key === 'Delete') && withoutExtraMetaKeys(e));
}
function patchKeyDownControls(jexcel) {
    const keyDownControls = jexcel.keyDownControls;
    jexcel.keyDownControls = (e) => {
        if (!isHistoryEnabled(jexcel) &&
            (isRedoHotkeyPressed(e) || isUndoHotkeyPressed(e))) {
            return;
        }
        if (hasOpenCustomEditor(jexcel) &&
            hasSelectedCell(jexcel) &&
            isEnterKeyPressed(e)) {
            const indexes = getCellIndexes(jexcel.current.selectedCell);
            if (!indexes)
                return;
            const { column, row } = indexes;
            const nextRowIndex = row + 1;
            jexcel.current.closeEditor(jexcel.current.records[row][column], true);
            e.preventDefault();
            e.stopPropagation();
            if (jexcel.current.records.length - 1 >= nextRowIndex) {
                jexcel.current.updateSelection(jexcel.current.records[nextRowIndex][column], void 0);
                return;
            }
        }
        if (hasOpenCustomEditor(jexcel) &&
            hasSelectedCell(jexcel) &&
            isTabKeyPressed(e)) {
            const indexes = getCellIndexes(jexcel.current.selectedCell);
            if (!indexes)
                return;
            const { column, row } = indexes;
            const nextColumnIndex = column + 1;
            jexcel.current.closeEditor(jexcel.current.records[row][column], true);
            e.preventDefault();
            e.stopPropagation();
            if (jexcel.current.records[row].length - 1 >= nextColumnIndex) {
                jexcel.current.updateSelection(jexcel.current.records[row][nextColumnIndex], void 0);
                return;
            }
        }
        if (hasOpenCustomEditor(jexcel) &&
            hasSelectedCell(jexcel) &&
            isEscapeKeyPressed(e)) {
            jexcel.current.closeEditor(jexcel.current.edition[0], false);
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (!isEditing(jexcel) &&
            hasSelectedCell(jexcel) &&
            isRedoHotkeyPressed(e)) {
            jexcel.current.redo();
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (!isEditing(jexcel) &&
            hasHighlightedCells(jexcel) &&
            isEditable(jexcel) &&
            isDeleteKeyPressed(e)) {
            jexcel.current.setValue(jexcel.current.highlighted, '');
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (!isEditing(jexcel) &&
            hasSelectedCell(jexcel) &&
            isEditable(jexcel) &&
            isEnterKeyPressed(e)) {
            const indexes = getCellIndexes(jexcel.current.selectedCell);
            if (!indexes)
                return;
            const { column, row } = indexes;
            jexcel.current.openEditor(jexcel.current.records[row][column], false);
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        keyDownControls(e);
    };
    return jexcel;
}

function isPrimaryButton(e) {
    const button = e.buttons || e.button || e.which;
    return button === 1;
}
function targetIsSheetHeaders(e, jexcel) {
    const target = e.target;
    const jexcelTable = jexcel.getElement(e.target);
    return ((jexcelTable[1] === 1 ||
        (jexcelTable[1] === 2 && target.classList.contains('jexcel_row'))) &&
        !!(target.getAttribute('data-x') ||
            target.getAttribute('data-y') ||
            target.classList.contains('jexcel_selectall')));
}
function targetIsInsideCustomEditor(e, jexcel) {
    if (!isEditing(jexcel) ||
        !jexcel.current.options.columns ||
        !jexcel.current.edition) {
        return false;
    }
    const col = parseInt(jexcel.current.edition[2], 10);
    if (isNaN(col))
        return false;
    const editor = jexcel.current.options.columns[col].editor;
    if (!editor || !editor.element)
        return false;
    let target = e.target;
    while ((target = target.parentElement)) {
        if (target === editor.element)
            return true;
    }
    return false;
}
function targetIsInSheet(e, jexcel) {
    if (!jexcel.current)
        return false;
    let target = e.target;
    while ((target = target.parentElement)) {
        if (target === jexcel.current.content)
            return true;
    }
    if (targetIsSheetHeaders(e, jexcel))
        return true;
    return targetIsInsideCustomEditor(e, jexcel);
}
function targetIsOutsideSheetWhileEditing(e, jexcel) {
    return isEditing(jexcel) && !targetIsInSheet(e, jexcel);
}
function clickedSheetHeadersWhileEditingInCustomEditor(e, jexcel) {
    return (isPrimaryButton(e) &&
        hasOpenCustomEditor(jexcel) &&
        targetIsSheetHeaders(e, jexcel));
}
function patchMouseDownControls(jexcel) {
    const mouseDownControls = jexcel.mouseDownControls;
    jexcel.mouseDownControls = (e) => {
        if (targetIsInsideCustomEditor(e, jexcel))
            return;
        if (isEditing(jexcel) &&
            jexcel.current.edition &&
            (clickedSheetHeadersWhileEditingInCustomEditor(e, jexcel) ||
                targetIsOutsideSheetWhileEditing(e, jexcel))) {
            jexcel.current.closeEditor(jexcel.current.edition[0], true);
        }
        mouseDownControls(e);
    };
    return jexcel;
}

const patches = [
    patchContextMenuControls,
    patchKeyDownControls,
    patchMouseDownControls,
];
function applyPatches(jexcel) {
    return patches.reduce((j, patch) => patch(j), jexcel);
}

function isCellVisible(sheet, cell, tolerance = 0) {
    const content = sheet.content;
    const position = cell.getBoundingClientRect();
    const boundary = content.getBoundingClientRect();
    const header = sheet.thead.getBoundingClientRect();
    const x = boundary.left - tolerance <= position.left &&
        boundary.right + tolerance >= position.right;
    const y = boundary.top + header.height - tolerance <= position.top &&
        boundary.bottom + tolerance >= position.bottom;
    return x && y;
}

function createAdjustEditorPositionHandler(zone, editor, sheet, tolerance = 0) {
    return () => {
        if (!sheet || !sheet.edition)
            return;
        zone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
                if (!sheet.edition)
                    return;
                const cell = sheet.edition[0];
                if (!isCellVisible(sheet, cell, tolerance))
                    sheet.closeEditor(cell, true);
                else
                    editor.move(cell.offsetTop - sheet.content.scrollTop, cell.offsetLeft - sheet.content.scrollLeft);
            });
        });
    };
}
function useEditor(zone, editor, sheet, tolerance = 0) {
    if (!sheet)
        return sheet;
    // JSpreadsheet now closes the editor on scroll,
    // but our implementation depends on the editor to be kept open on scroll.
    const scrollControls = sheet.scrollControls;
    sheet.scrollControls = e => {
        if (!!sheet.edition)
            return;
        scrollControls(e);
    };
    const openEditor = sheet.openEditor;
    sheet.openEditor = (cell, empty, e) => {
        if (!sheet.options.columns)
            return;
        const x = parseInt(cell.getAttribute('data-x') ?? '0', 10);
        const y = parseInt(cell.getAttribute('data-y') ?? '0', 10);
        const scrollHandler = createAdjustEditorPositionHandler(zone, editor, sheet, tolerance);
        sheet.options.columns[x].editor = {
            element: editor.element,
            openEditor: () => {
                zone.runOutsideAngular(() => {
                    const open = () => {
                        const value = !empty ? (sheet.getValueFromCoords(x, y) ?? '') : '';
                        sheet.content.addEventListener('scroll', scrollHandler);
                        editor.setValue(`${value}`);
                        editor.show(cell, sheet);
                        editor.focus();
                    };
                    if (isCellVisible(sheet, cell))
                        open();
                    else {
                        cell.scrollIntoView({
                            behavior: 'instant',
                            block: 'nearest',
                            inline: 'nearest',
                        });
                        setTimeout(open);
                    }
                });
            },
            closeEditor: () => {
                sheet.content.removeEventListener('scroll', scrollHandler);
                const value = editor.getValue();
                if (!!sheet.options.columns) {
                    sheet.options.columns[x].editor = void 0;
                }
                editor.hide();
                editor.blur();
                editor.setValue('');
                return value;
            },
        };
        openEditor(cell, empty, e);
    };
    return sheet;
}

function addTranspose(jexcel, options) {
    if (!jexcel)
        return;
    jexcel.transpose = () => {
        const data = jexcel.getData();
        jexcel.setData(transpose(shrink(data)));
        if (options && options.ontranspose)
            options.ontranspose();
    };
}

applyPatches(JSpreadsheet);
const defaults = {
    data: [['']],
    allowComments: false,
    columnSorting: false,
    search: false,
    wordWrap: true,
    tableOverflow: true,
    tableHeight: '100%',
    tableWidth: '100%',
    defaultColWidth: 100,
    minSpareCols: 1,
    minSpareRows: 1,
    minDimensions: [10, 6],
    contextMenu: createContextMenu(DEFAULT_MENU_ITEMS),
    scrollTolerance: 15,
    allowHistory: true,
};
function clearNilOptions(options) {
    return Object.keys(options).reduce((result, key) => {
        const value = options[key];
        if (value === null || value === undefined)
            return result;
        result[key] = value;
        return result;
    }, {});
}
function populateAlignments(data) {
    if (!data)
        return [];
    const row = data[0] ?? [];
    return (Array.isArray(row) ? row : Object.values(row)).map(() => 'left');
}
class JExcelAdapter {
    constructor(zone, element, editor, options) {
        this.zone = zone;
        this.element = element;
        this.editor = editor;
        const colAlignments = populateAlignments((options || {})['data']);
        this.options = {
            ...defaults,
            ...{ colAlignments },
            ...clearNilOptions(options),
        };
        this.sheet = this.zone.runOutsideAngular(() => JSpreadsheet(this.element, this.options));
        this.sheet.ignoreHistory = !this.options.allowHistory;
        // Remove handlers as they close the editor when they are triggered and we don't want that behavior.
        this.element.removeEventListener('DOMMouseScroll', this.sheet.scrollControls);
        this.element.removeEventListener('mousewheel', this.sheet.scrollControls);
        useEditor(this.zone, this.editor, this.sheet, this.options.scrollTolerance);
        addTranspose(this.sheet, this.options);
    }
    data() {
        return shrink(this.sheet.getData());
    }
    setData(data) {
        const shrinked = shrink(data);
        if (isEqual(this.data(), shrinked))
            return;
        this.sheet.setData(shrinked);
    }
    closeEditor() {
        if (!this.sheet || !this.sheet.edition)
            return;
        this.sheet.closeEditor(this.sheet.edition[0], true);
    }
    transpose() {
        if (!this.sheet || !this.sheet.transpose)
            return;
        this.sheet.transpose();
    }
    destroy() {
        JSpreadsheet.destroy(this.element);
    }
}

function didChange(change) {
    return (change &&
        !change.isFirstChange() &&
        change.previousValue !== change.currentValue);
}
var ColumnType;
(function (ColumnType) {
    ColumnType["Text"] = "text";
    ColumnType["Numeric"] = "numeric";
})(ColumnType || (ColumnType = {}));
class SpreadsheetComponent {
    constructor(zone) {
        this.zone = zone;
        this.cells = [['']];
        this.useWysiwyg = true;
        this.columnWidth = 100;
        this.minRows = 6;
        this.minColumns = 10;
        this.minSpareRows = 1;
        this.minSpareColumns = 1;
        this.history = true;
        this.allowDeleteColumn = true;
        this.allowInsertColumn = true;
        this.allowDeleteRow = true;
        this.allowInsertRow = true;
        this.onChange = new EventEmitter();
        this.onCellRender = new EventEmitter();
        this.flushChanges = this.flushChanges.bind(this);
        this.updateTable = this.updateTable.bind(this);
    }
    ngAfterViewInit() {
        const options = {
            data: clone(this.cells),
            columns: (this.columns ?? []).map(config => ({
                ...config,
                align: config.type === ColumnType.Numeric ? 'right' : 'left',
                mask: config.type === ColumnType.Numeric ? '#,##.00' : void 0,
                decimal: config.type === ColumnType.Numeric ? '.' : void 0,
            })),
            onchange: this.flushChanges,
            oninsertrow: this.flushChanges,
            oninsertcolumn: this.flushChanges,
            ondeleterow: this.flushChanges,
            ondeletecolumn: this.flushChanges,
            onmoverow: this.flushChanges,
            onmovecolumn: this.flushChanges,
            onundo: this.flushChanges,
            onredo: this.flushChanges,
            ontranspose: this.flushChanges,
            updateTable: this.updateTable,
            minDimensions: [this.minColumns, this.minRows],
            scrollTolerance: this.scrollTolerance,
            defaultColWidth: this.columnWidth,
            minSpareRows: this.minSpareRows,
            minSpareCols: this.minSpareColumns,
            contextMenu: this.contextMenu
                ? createContextMenu(this.contextMenu)
                : void 0,
            allowHistory: this.history,
            allowDeleteColumn: this.allowDeleteColumn,
            allowInsertColumn: this.allowInsertColumn,
            allowDeleteRow: this.allowDeleteRow,
            allowInsertRow: this.allowInsertRow,
            stripHTML: !this.useWysiwyg,
        };
        this.jexcel = new JExcelAdapter(this.zone, this.element.nativeElement, this.editor, options);
    }
    ngOnChanges(changes) {
        if (!didChange(changes['cells']))
            return;
        this.jexcel.setData(this.cells);
    }
    ngOnDestroy() {
        if (!this.jexcel)
            return;
        this.jexcel.destroy();
    }
    flushChanges() {
        if (!this.jexcel)
            return;
        this.zone.run(() => this.onChange.emit(this.jexcel.data()));
    }
    transpose() {
        if (!this.jexcel || !this.jexcel.transpose)
            return;
        this.jexcel.transpose();
    }
    updateTable(instance, cell, column, row, data, text, cellName) {
        this.onCellRender.emit({
            instance,
            cell,
            row,
            column,
            data,
            text,
            cellName,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SpreadsheetComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.3", type: SpreadsheetComponent, selector: "sb-spreadsheet", inputs: { cells: "cells", columns: "columns", useWysiwyg: "useWysiwyg", columnWidth: "columnWidth", minRows: "minRows", minColumns: "minColumns", minSpareRows: "minSpareRows", minSpareColumns: "minSpareColumns", contextMenu: "contextMenu", scrollTolerance: "scrollTolerance", history: "history", allowDeleteColumn: "allowDeleteColumn", allowInsertColumn: "allowInsertColumn", allowDeleteRow: "allowDeleteRow", allowInsertRow: "allowInsertRow" }, outputs: { onChange: "onChange", onCellRender: "onCellRender" }, viewQueries: [{ propertyName: "element", first: true, predicate: ["spreadsheet"], descendants: true }, { propertyName: "editor", first: true, predicate: CellEditorComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <div #spreadsheet class="spreadsheet"></div>
    <sb-cell-editor
      [useWysiwyg]="useWysiwyg"
      style="position: absolute; display: none; font-size: 1em; z-index: 2100"
    ></sb-cell-editor>
  `, isInline: true, styles: [":host{position:relative;display:block;width:100%;height:100%}.spreadsheet::ng-deep{display:flex;flex-direction:column;padding-right:0}.spreadsheet::ng-deep .jexcel_contextmenu .jcontextmenu{font-family:inherit;box-shadow:none;border-radius:.25rem;border:1px solid #dee2e6;min-width:10em}.spreadsheet::ng-deep .jexcel_contextmenu .jcontextmenu li{width:100%;padding:.25rem 1.5rem;cursor:pointer;white-space:nowrap}.spreadsheet::ng-deep .jexcel_contextmenu .jcontextmenu li>*{white-space:nowrap}.spreadsheet::ng-deep .jexcel_corner{background-color:#52bef2;z-index:1}.spreadsheet::ng-deep .jexcel_content{padding-right:0;padding-bottom:15px}.spreadsheet::ng-deep .jexcel_content>table{min-width:100%}.spreadsheet::ng-deep .jexcel_content td{color:#596377;overflow:hidden}.spreadsheet::ng-deep .jexcel_content td.readonly{color:#aab1bf!important;background-color:#aab1bf1a!important}.spreadsheet::ng-deep .jexcel_content td>*:last-child{margin-bottom:0}.spreadsheet::ng-deep .jexcel_content thead td{text-align:center!important;z-index:1}.spreadsheet::ng-deep .jexcel_content thead td,.spreadsheet::ng-deep .jexcel_content tbody .jexcel_row{color:#aab1bf}.spreadsheet::ng-deep .jexcel_content td:last-child,.spreadsheet::ng-deep .jexcel_content th:last-child{border-right:1px solid #ccc}.spreadsheet::ng-deep .jexcel_content td,.spreadsheet::ng-deep .jexcel_content th{padding:2px 4px}.spreadsheet::ng-deep .jexcel_content tbody tr:last-child td{border-bottom:1px solid #ccc}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight{background-color:#52bef233}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-selected{background-color:#52bef200}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-top{border-top:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-right{border-right:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-left{border-left:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-bottom{border-bottom:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection{background-color:#52bef21a}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-top{border-top:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-right{border-right:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-left{border-left:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-bottom{border-bottom:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content .jexcel{border:0;border-spacing:0;margin:0;border-width:0;outline-width:0}.spreadsheet::ng-deep .jcontextmenu-disabled{color:#aab1bf;pointer-events:none;cursor:default}.spreadsheet::ng-deep .jcontextmenu-disabled:hover{background-color:inherit}.spreadsheet::ng-deep .jexcel_about,.spreadsheet::ng-deep .jexcel_toolbar,.spreadsheet::ng-deep .jexcel_pagination{display:none}\n"], dependencies: [{ kind: "component", type: CellEditorComponent, selector: "sb-cell-editor", inputs: ["useWysiwyg"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SpreadsheetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-spreadsheet', template: `
    <div #spreadsheet class="spreadsheet"></div>
    <sb-cell-editor
      [useWysiwyg]="useWysiwyg"
      style="position: absolute; display: none; font-size: 1em; z-index: 2100"
    ></sb-cell-editor>
  `, styles: [":host{position:relative;display:block;width:100%;height:100%}.spreadsheet::ng-deep{display:flex;flex-direction:column;padding-right:0}.spreadsheet::ng-deep .jexcel_contextmenu .jcontextmenu{font-family:inherit;box-shadow:none;border-radius:.25rem;border:1px solid #dee2e6;min-width:10em}.spreadsheet::ng-deep .jexcel_contextmenu .jcontextmenu li{width:100%;padding:.25rem 1.5rem;cursor:pointer;white-space:nowrap}.spreadsheet::ng-deep .jexcel_contextmenu .jcontextmenu li>*{white-space:nowrap}.spreadsheet::ng-deep .jexcel_corner{background-color:#52bef2;z-index:1}.spreadsheet::ng-deep .jexcel_content{padding-right:0;padding-bottom:15px}.spreadsheet::ng-deep .jexcel_content>table{min-width:100%}.spreadsheet::ng-deep .jexcel_content td{color:#596377;overflow:hidden}.spreadsheet::ng-deep .jexcel_content td.readonly{color:#aab1bf!important;background-color:#aab1bf1a!important}.spreadsheet::ng-deep .jexcel_content td>*:last-child{margin-bottom:0}.spreadsheet::ng-deep .jexcel_content thead td{text-align:center!important;z-index:1}.spreadsheet::ng-deep .jexcel_content thead td,.spreadsheet::ng-deep .jexcel_content tbody .jexcel_row{color:#aab1bf}.spreadsheet::ng-deep .jexcel_content td:last-child,.spreadsheet::ng-deep .jexcel_content th:last-child{border-right:1px solid #ccc}.spreadsheet::ng-deep .jexcel_content td,.spreadsheet::ng-deep .jexcel_content th{padding:2px 4px}.spreadsheet::ng-deep .jexcel_content tbody tr:last-child td{border-bottom:1px solid #ccc}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight{background-color:#52bef233}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-selected{background-color:#52bef200}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-top{border-top:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-right{border-right:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-left{border-left:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.highlight-bottom{border-bottom:1px solid #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection{background-color:#52bef21a}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-top{border-top:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-right{border-right:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-left{border-left:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content tbody tr>td.selection-bottom{border-bottom:1px dotted #52bef2}.spreadsheet::ng-deep .jexcel_content .jexcel{border:0;border-spacing:0;margin:0;border-width:0;outline-width:0}.spreadsheet::ng-deep .jcontextmenu-disabled{color:#aab1bf;pointer-events:none;cursor:default}.spreadsheet::ng-deep .jcontextmenu-disabled:hover{background-color:inherit}.spreadsheet::ng-deep .jexcel_about,.spreadsheet::ng-deep .jexcel_toolbar,.spreadsheet::ng-deep .jexcel_pagination{display:none}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }], propDecorators: { cells: [{
                type: Input
            }], columns: [{
                type: Input
            }], useWysiwyg: [{
                type: Input
            }], columnWidth: [{
                type: Input
            }], minRows: [{
                type: Input
            }], minColumns: [{
                type: Input
            }], minSpareRows: [{
                type: Input
            }], minSpareColumns: [{
                type: Input
            }], contextMenu: [{
                type: Input
            }], scrollTolerance: [{
                type: Input
            }], history: [{
                type: Input
            }], allowDeleteColumn: [{
                type: Input
            }], allowInsertColumn: [{
                type: Input
            }], allowDeleteRow: [{
                type: Input
            }], allowInsertRow: [{
                type: Input
            }], onChange: [{
                type: Output
            }], onCellRender: [{
                type: Output
            }], element: [{
                type: ViewChild,
                args: ['spreadsheet']
            }], editor: [{
                type: ViewChild,
                args: [CellEditorComponent]
            }] } });

const DIRECTIVES = [CellEditorComponent, SpreadsheetComponent];
class SpreadsheetModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SpreadsheetModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.3", ngImport: i0, type: SpreadsheetModule, declarations: [CellEditorComponent, SpreadsheetComponent], imports: [CommonModule, WysiwygModule], exports: [CellEditorComponent, SpreadsheetComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SpreadsheetModule, imports: [CommonModule, WysiwygModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SpreadsheetModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, WysiwygModule],
                    declarations: DIRECTIVES,
                    exports: DIRECTIVES,
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CellEditorComponent, ColumnType, DEFAULT_MENU_ITEMS, DELETE_COLUMN, DELETE_ROW, INSERT_COLUMN_AFTER, INSERT_COLUMN_BEFORE, INSERT_ROW_AFTER, INSERT_ROW_BEFORE, SEPARATOR, SpreadsheetComponent, SpreadsheetModule, TRANSPOSE, clone, createContextMenu, createMenuItem, factories, isFactory, isToken, shrink, transpose };
